import type { FormEvent } from 'react';
import type { SignupDetails, SignupDetailsErrors } from './signup-validation';
import { ArrowRight } from 'lucide-react';
import { AuthField, AuthPasswordField, AuthPrimaryButton } from '../AuthControls';
import { PASSWORD_HINT } from './signup-validation';

type SignupDetailsCardProps = {
  details: SignupDetails;
  errors: SignupDetailsErrors;
  onChange: (name: keyof SignupDetails, value: string) => void;
  onSubmit: () => void;
};

/**
 * The details form, rendered as a card in the thread. Validated on submit, with
 * every problem shown at once under its own field: the card is the only place the
 * visitor will see them, and revealing one error per attempt is a slow way to learn
 * what a password needs.
 *
 * noValidate because the browser's own bubbles would pre-empt these messages and
 * disagree with them (type="email" accepts a@b, the server does not).
 */
export const SignupDetailsCard = ({ details, errors, onChange, onSubmit }: SignupDetailsCardProps) => {
  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    onSubmit();
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="pt-bubble-in bg-panel mt-[18px] ml-[38px] grid grid-cols-2 gap-x-3.5 gap-y-[18px] rounded-[16px] p-[22px] max-sm:ml-0 max-sm:grid-cols-1"
      data-from="pt"
    >
      {/* Ids are prefixed because the sign-in form shares this page and also has an
          email and a password; two elements with the same id make a label click
          focus the wrong one. The first field takes focus: the card appears in
          answer to a tap, and the next thing anyone does is type. */}
      <AuthField
        id="signup-first"
        autoFocus
        label="First name"
        name="first"
        autoComplete="given-name"
        value={details.first}
        error={errors.first}
        onChange={(ev) => onChange('first', ev.target.value)}
      />
      <AuthField
        id="signup-last"
        label="Last name"
        name="last"
        autoComplete="family-name"
        value={details.last}
        error={errors.last}
        onChange={(ev) => onChange('last', ev.target.value)}
      />

      <div className="col-span-full max-sm:col-span-1">
        <AuthField
          id="signup-email"
          label="Work email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={details.email}
          error={errors.email}
          onChange={(ev) => onChange('email', ev.target.value)}
        />
      </div>

      <AuthPasswordField
        id="signup-password"
        label="Password"
        name="password"
        autoComplete="new-password"
        hint={PASSWORD_HINT}
        value={details.password}
        error={errors.password}
        onChange={(ev) => onChange('password', ev.target.value)}
      />
      <AuthPasswordField
        id="signup-confirm"
        label="Confirm password"
        name="confirm"
        autoComplete="new-password"
        value={details.confirm}
        error={errors.confirm}
        onChange={(ev) => onChange('confirm', ev.target.value)}
      />

      <div className="col-span-full max-sm:col-span-1">
        <AuthField
          id="signup-company"
          label="Company name"
          name="company"
          autoComplete="organization"
          value={details.company}
          error={errors.company}
          onChange={(ev) => onChange('company', ev.target.value)}
        />
      </div>

      <div className="col-span-full max-sm:col-span-1">
        <AuthPrimaryButton trailingIcon={<ArrowRight size={16} strokeWidth={1.5} />}>Continue</AuthPrimaryButton>
      </div>
    </form>
  );
};
